import { useEffect, useRef, useState } from "react";

import type { ModuleKey } from "@/lib/grid-contract";
import type { WeeklyPlannerPayload } from "@/lib/api";
import {
  subscribeWeeklyPlannerRealtime,
  type WeeklyPlannerPresenceUser,
  type WeeklyPlannerRealtimeSubscription,
} from "@/lib/realtime/weekly-planner-cable";

type RealtimeConnectionState = "connecting" | "connected" | "disconnected" | "rejected";

type UseRealtimeCollaborationArgs = {
  activeModule: ModuleKey;
  weeklyPlanner: WeeklyPlannerPayload | null;
  sourceClientId: string;
  onRemotePlannerChanged: (planner: WeeklyPlannerPayload) => void;
  onRemoteMyProjectsChanged: (weekStartOn: string) => Promise<WeeklyPlannerPayload>;
};

export function useRealtimeCollaboration({
  activeModule,
  weeklyPlanner,
  sourceClientId,
  onRemotePlannerChanged,
  onRemoteMyProjectsChanged,
}: UseRealtimeCollaborationArgs) {
  const [presenceUsers, setPresenceUsers] = useState<WeeklyPlannerPresenceUser[]>([]);
  const [, setRealtimeConnectionState] = useState<RealtimeConnectionState>("disconnected");
  const [realtimeMessage, setRealtimeMessage] = useState<string | null>(null);
  const realtimeSubscriptionRef = useRef<WeeklyPlannerRealtimeSubscription | null>(null);
  const realtimePingIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    const teardownRealtime = () => {
      if (realtimePingIntervalRef.current !== null) {
        window.clearInterval(realtimePingIntervalRef.current);
        realtimePingIntervalRef.current = null;
      }
      if (realtimeSubscriptionRef.current) {
        realtimeSubscriptionRef.current.unsubscribe();
        realtimeSubscriptionRef.current = null;
      }
      setPresenceUsers([]);
      setRealtimeConnectionState("disconnected");
    };

    if (!(activeModule === "weekly_planner" || activeModule === "my_projects") || !weeklyPlanner) {
      teardownRealtime();
      return teardownRealtime;
    }

    teardownRealtime();
    realtimeSubscriptionRef.current = subscribeWeeklyPlannerRealtime({
      weekStartOn: weeklyPlanner.week_start_on,
      sourceClientId,
      onConnectionState: setRealtimeConnectionState,
      onChannelConnected: () => {
        realtimeSubscriptionRef.current?.sendPresencePing();
        realtimePingIntervalRef.current = window.setInterval(() => {
          realtimeSubscriptionRef.current?.sendPresencePing();
        }, 15_000);
      },
      onEvent: (event) => {
        if (event.event_type === "presence_snapshot") {
          setPresenceUsers(event.users);
          return;
        }

        if (event.source_client_id === sourceClientId) return;
        if (activeModule === "my_projects") {
          onRemoteMyProjectsChanged(weeklyPlanner.week_start_on)
            .then((payload) => onRemotePlannerChanged(payload))
            .catch(() =>
              setRealtimeMessage("Planner updated. Refresh to sync latest My Projects view."),
            );
        } else {
          onRemotePlannerChanged(event.planner);
        }
        setRealtimeMessage(`Updated by ${event.actor_user_email}`);
        window.setTimeout(() => setRealtimeMessage(null), 2200);
      },
    });

    return teardownRealtime;
  }, [activeModule, sourceClientId, weeklyPlanner?.week_start_on]);

  function publishActiveCellChanged(rowId: string, columnId: string) {
    realtimeSubscriptionRef.current?.sendActiveCellChanged({ rowId, columnId });
  }

  return {
    presenceUsers,
    realtimeMessage,
    publishActiveCellChanged,
  };
}
